
import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {

  connectedCallback() {
    super.connectedCallback();

    //  Sync price when cart drawer quantity changes
    document.addEventListener('cart:updated', () => {
      this.updateDisplayedPrice();
    });
  }

  /**
   * Handles the quantity increase event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * Selects all input value on focus.
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles direct input changes.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }

    this.#onQuantityChange();
  }

  /**
   * Handles any quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    // ⭐ Update product page price
    this.updateDisplayedPrice();

    // ⭐ Update REAL Shopify form quantity so cart receives correct qty
    const formQuantity = document.querySelector('form [name="quantity"]');
    if (formQuantity) {
      formQuantity.value = newValue;
    }

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * Enforces min/max rules.
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) quantityInput.value = max;
  };

  /**
   *  Updates product page price when quantity changes OR when cart changes.
   */
  updateDisplayedPrice() {
    const priceEl = document.querySelector('.price');
    if (!priceEl) return;

    const unitPrice = parseFloat(priceEl.dataset.unitPrice);
    if (!unitPrice) return;

    //  Read quantity from product page input
    let qty = parseInt(this.refs.quantityInput?.value || 1);

    //  If cart drawer has a different quantity, sync with cart drawer
    const cartQtyInput = document.querySelector('[data-quantity-input]');
    if (cartQtyInput) {
      qty = parseInt(cartQtyInput.value);
    }

    const total = unitPrice * qty;
    priceEl.textContent = `£${total.toFixed(2)}`;
  }

  /**
   * Accessor for quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }
    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
